import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../common';
import { Card } from '../../common/Card';
import { Heading, Typography } from '../../common/Typography';
import { format, parseISO } from 'date-fns';
import { homeSectionHeading, homeElevatedInset } from '../../home/homeDashboardStyles';
import {
  buildEventHeadline,
  buildEventSubline,
  getEventIcon,
  getLadderLabel,
  isMatchupEvent,
} from '../../../services/activityFeedService';
import {
  getActivityMomentCta,
  navigateForActivityMoment,
} from '../../../utils/activityMomentNavigation';
import { NotableWinSubline } from '../../activity/NotableWinSubline';
import { interactiveBleedInsetX } from '../../../constants/interactiveSafeInsets';

const clubMomentsStripClass = `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x scrollbar-hide-mobile`;
const clubMomentsCardClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start';

const EVENT_COLOURS = {
  personal_best:          ['bg-amber-100 dark:bg-amber-900/30',    'text-amber-700 dark:text-amber-400'],
  rank_up:                ['bg-sky-100 dark:bg-sky-900/30',        'text-sky-700 dark:text-sky-400'],
  overtook_player:        ['bg-violet-100 dark:bg-violet-900/30',  'text-violet-700 dark:text-violet-400'],
  undefeated_session:     ['bg-emerald-100 dark:bg-emerald-900/30','text-emerald-700 dark:text-emerald-400'],
  great_session:          ['bg-orange-100 dark:bg-orange-900/30',  'text-orange-700 dark:text-orange-400'],
  biggest_mover:          ['bg-teal-100 dark:bg-teal-900/30',      'text-teal-700 dark:text-teal-400'],
  matchup_first_win:      ['bg-teal-100 dark:bg-teal-900/30',      'text-teal-700 dark:text-teal-400'],
  matchup_tied:           ['bg-sky-100 dark:bg-sky-900/30',        'text-sky-700 dark:text-sky-400'],
  matchup_took_lead:      ['bg-sky-100 dark:bg-sky-900/30',        'text-sky-700 dark:text-sky-400'],
  matchup_hot_form:       ['bg-teal-100 dark:bg-teal-900/30',      'text-teal-700 dark:text-teal-400'],
  matchup_frequent_recent:['bg-slate-100 dark:bg-slate-800/60',    'text-slate-600 dark:text-slate-300'],
  rivalry_swing:          ['bg-sky-100 dark:bg-sky-900/30',        'text-sky-700 dark:text-sky-400'],
  rivalry_heated:         ['bg-teal-100 dark:bg-teal-900/30',      'text-teal-700 dark:text-teal-400'],
  rivalry_nemesis:        ['bg-slate-100 dark:bg-slate-800/60',    'text-slate-600 dark:text-slate-300'],
  // v2 new types
  upset_win:              ['bg-rose-100 dark:bg-rose-900/30',      'text-rose-700 dark:text-rose-400'],
  close_game_loss:        ['bg-slate-100 dark:bg-slate-800/60',    'text-slate-600 dark:text-slate-300'],
  session_new_opponent:   ['bg-cyan-100 dark:bg-cyan-900/30',      'text-cyan-700 dark:text-cyan-400'],
  session_played_with:    ['bg-slate-100 dark:bg-slate-800/60',    'text-slate-600 dark:text-slate-300'],
};

function getEventColours(eventType) {
  return EVENT_COLOURS[eventType] ?? ['bg-surface-subtle', 'text-text-secondary'];
}

function MomentCard({ event, currentUser, compact = false }) {
  const navigate = useNavigate();
  const [bgClass, textClass] = getEventColours(event.event_type);
  const icon = getEventIcon(event.event_type);
  const headline = buildEventHeadline(event);
  const subline = buildEventSubline(event);
  const isNotableWin = event.event_type === 'upset_win';
  const isMatchup = isMatchupEvent(event.event_type);
  const ladderLabel = isMatchup
    ? 'Matchup'
    : event.metadata?.ladder_type
      ? getLadderLabel(event.metadata.ladder_type)
      : null;
  const dateStr = event.occurred_at ? format(parseISO(event.occurred_at), 'MMM d') : null;
  const momentCta = getActivityMomentCta(event, { currentUser });

  function handleClick() {
    navigateForActivityMoment(navigate, event, currentUser);
  }

  return (
    <Card
      variant="default"
      size={compact ? 'small' : 'medium'}
      className={`flex flex-col ${compact ? 'gap-2 shadow-none bg-surface-subtle/40' : `gap-3 ${homeElevatedInset}`} ${clubMomentsCardClass}`}
    >
      {/* Icon chip + type label */}
      <div className="flex items-center gap-2.5">
        <span
          className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-lg shrink-0 ${bgClass} ${textClass}`}
          aria-hidden
        >
          {icon}
        </span>
        <Typography variant="caption" className="text-text-muted uppercase tracking-wide text-[0.65rem] font-semibold truncate">
          {ladderLabel ?? 'Moment'}
        </Typography>
      </div>

      {/* Headline */}
      <Typography className="font-semibold leading-snug text-text-primary text-sm line-clamp-3">
        {headline}
      </Typography>

      {isNotableWin ? (
        <NotableWinSubline event={event} />
      ) : subline ? (
        <Typography variant="caption" className="text-text-secondary leading-snug line-clamp-4">
          {subline}
        </Typography>
      ) : null}

      {/* Date */}
      {dateStr && (
        <div className="flex items-end mt-auto">
          <Typography variant="caption" className="text-text-muted">{dateStr}</Typography>
        </div>
      )}

      {momentCta ? (
        <div className={dateStr ? '' : 'mt-auto'}>
          <Button
            variant="outline"
            size="small"
            className="w-full text-xs font-semibold"
            onClick={handleClick}
          >
            {momentCta.label}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Your moments carousel for My Club dashboard — horizontal snap scroll on mobile (matches homepage).
 *
 * @param {boolean} [embedded=false] - When true, omits the outer heading so the carousel can be
 *   used inside a parent container (e.g. ClubActivitySection) that already has a section heading.
 */
export function ClubMomentsStrip({ moments, loading, currentUser, embedded = false }) {
  if (loading) {
    if (embedded) {
      return (
        <div className="text-center py-6 rounded-xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">Loading moments...</Typography>
        </div>
      );
    }
    return (
      <div>
        <Heading level={2} className={`${homeSectionHeading} mb-5`}>Your Moments</Heading>
        <div className="text-center py-8 rounded-2xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">Loading moments...</Typography>
        </div>
      </div>
    );
  }

  if (!moments || moments.length === 0) return null;

  if (embedded) {
    return (
      <>
        <div
          role="region"
          aria-label="Activity moments"
          className={clubMomentsStripClass}
        >
          {moments.slice(0, 5).map((event) => (
            <MomentCard key={event.id} event={event} currentUser={currentUser} compact />
          ))}
        </div>
        {moments.length > 1 && (
          <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
            Swipe for more highlights
          </Typography>
        )}
      </>
    );
  }

  return (
    <div>
      <Heading level={2} className={`${homeSectionHeading} mb-5`}>Your Moments</Heading>
      <div
        role="region"
        aria-label="Activity moments"
        className={clubMomentsStripClass}
      >
        {moments.slice(0, 5).map((event) => (
          <MomentCard key={event.id} event={event} currentUser={currentUser} />
        ))}
      </div>
      {moments.length > 1 && (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
          Swipe sideways for more · Each card has a button for more detail
        </Typography>
      )}
    </div>
  );
}
